import { useCallback, useEffect, useRef, useState } from "react";

export type GeolocationPermissionState = "unsupported" | "prompt" | "granted" | "denied";

export interface GeolocationCoords {
  lat: number;
  lng: number;
}

interface UseGeolocationResult {
  coords: GeolocationCoords | null;
  permissionState: GeolocationPermissionState;
}

/**
 * Tracks the browser's geolocation permission and, once granted, the
 * device's current coordinates. Never prompts on mount by itself when the
 * browser doesn't support the Permissions API - it falls back to asking
 * navigator.geolocation directly, which shows the native prompt itself.
 */
export default function useGeolocation(enabled: boolean): UseGeolocationResult {
  const [coords, setCoords] = useState<GeolocationCoords | null>(null);
  const [permissionState, setPermissionState] = useState<GeolocationPermissionState>(
    typeof navigator !== "undefined" && "geolocation" in navigator ? "prompt" : "unsupported"
  );
  const requestedRef = useRef(false);

  const requestPosition = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setPermissionState("unsupported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPermissionState("granted");
        setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => {
        setPermissionState("denied");
        setCoords(null);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  }, []);

  useEffect(() => {
    if (!enabled || requestedRef.current) {
      return;
    }
    requestedRef.current = true;

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setPermissionState("unsupported");
      return;
    }

    if (!navigator.permissions?.query) {
      requestPosition();
      return;
    }

    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((status) => {
        setPermissionState(status.state as GeolocationPermissionState);
        if (status.state !== "denied") {
          requestPosition();
        }
        status.onchange = () => {
          setPermissionState(status.state as GeolocationPermissionState);
          if (status.state === "granted") {
            requestPosition();
          } else if (status.state === "denied") {
            setCoords(null);
          }
        };
      })
      .catch(() => {
        requestPosition();
      });
  }, [enabled, requestPosition]);

  return { coords, permissionState };
}
